import { useQuery, gql } from '@apollo/client';

const HAS_GALLERIES = gql`
  query HasGalleries {
    me {
      organisation {
        id
      }
    }
    galleries {
      id
      organisationId
    }
  }
`;

/**
 * Can this person reach at least one gallery **in the organisation they are working in**?
 *
 * **The gallery menu entry used to be gated on `isContentReviewer`**, a role name, and only on
 * desktop — so anyone given access to a single gallery had no way in except knowing the URL,
 * and no way in at all on a phone. Access to a gallery is a grant, not a role: a centre manager
 * holds MANAGE over its community's galleries, a teacher holds CONTRIBUTE, and any custom role
 * can be given either on `/admin/roles`. Asking the server "what can you reach?" is the only
 * question that stays true as those change.
 *
 * **The organisation filter is not redundant.** `galleries` resolves grants through
 * `accessibleResourceIds` *without* `ownerOrganisationId`, which is deliberately the
 * cross-organisation view — so a grant written elsewhere can put another organisation's gallery
 * in that list. A menu entry offered on the strength of one would lead to a page scoped to the
 * working organisation, and therefore to nothing.
 */
export const useHasGalleries = (): boolean => {
  const { data, loading, error } = useQuery(HAS_GALLERIES);
  if (loading || error) return false;

  const organisationId = data?.me?.organisation?.id;
  if (organisationId == null) return false;

  return (data?.galleries ?? [])
    .some((gallery: any) => gallery?.organisationId === organisationId);
};
